import React, { useState } from "react";
import { FallbackProps } from "react-error-boundary";
import OwButton from "../OwButton/OwButton";
import OwLabel from "../OwLabel/OwLabel";
import UIService from "../../../services/UIService";
import "./ErrorFallback.scss";

export const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    const [showDetails, setShowDetails] = useState(false);
    const isDevelopment = process.env.NODE_ENV === "development";
    const isLightMode = UIService.isMainViewLight();

    const copyErrorToClipboard = () => {
        const errorText = `
Error: ${error.message}

Stack Trace:
${error.stack}

Time: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
        `;

        navigator.clipboard.writeText(errorText).then(() => {
            // Could add a toast notification here if available
            alert("Error details copied to clipboard");
        });
    };

    const toggleDetails = () => {
        setShowDetails(!showDetails);
    };

    const refreshPage = () => {
        window.location.reload();
    };

    return (
        <div className="ErrorFallback p-3">
            <div className="error-card">
                <div className="error-header">
                    <div className="d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle me-2 fa-lg"></i>
                        <h5 className="mb-0">Something went wrong</h5>
                    </div>
                </div>

                <div className="card-body">
                    <div className="text-center mb-4">
                        <i className="fas fa-tools fa-3x text-muted mb-3"></i>
                        <p className="text-muted">
                            {isDevelopment
                                ? "An error occurred while rendering this component."
                                : "We encountered an unexpected error. Please try again."}
                        </p>
                    </div>

                    {isDevelopment && (
                        <div className="alert alert-danger mb-3" role="alert">
                            <strong>
                                <OwLabel title={"error"} postText=":" />
                            </strong>{" "}
                            {error.message}
                        </div>
                    )}

                    <div className="d-flex justify-content-center gap-2 mb-3">
                        <OwButton title="try_again" className="btn-primary" leftIconClass="fas fa-redo" fn={resetErrorBoundary} />
                        <OwButton title="refresh_page" className="btn-secondary" leftIconClass="fas fa-sync-alt" fn={refreshPage} />
                        {isDevelopment && (
                            <OwButton
                                title="copy_error"
                                className="btn-outline-secondary"
                                leftIconClass="fas fa-clipboard"
                                fn={copyErrorToClipboard}
                            />
                        )}
                    </div>

                    {isDevelopment && (
                        <>
                            <div className="text-center">
                                <button className="btn btn-link btn-sm text-decoration-none" onClick={toggleDetails}>
                                    <i className={`fas fa-chevron-${showDetails ? "up" : "down"} me-1`}></i>
                                    {showDetails ? "Hide" : "Show"} Error Details
                                </button>
                            </div>

                            {showDetails && (
                                <div className="mt-3">
                                    <div className="error-code-block">
                                        <div className="code-block-body">
                                            <h6 className="code-block-title mb-2">Stack Trace:</h6>
                                            <pre
                                                className="code-block-content small mb-0"
                                                style={{
                                                    maxHeight: "300px",
                                                    overflowY: "auto",
                                                    fontSize: "0.85rem",
                                                    whiteSpace: "pre-wrap",
                                                    wordBreak: "break-word",
                                                }}>
                                                {error.stack}
                                            </pre>
                                        </div>
                                    </div>

                                    <div className="mt-2 small text-muted">
                                        <div>
                                            <strong>
                                                <OwLabel title={"time"} postText=":" />
                                            </strong>{" "}
                                            {new Date().toLocaleString()}
                                        </div>
                                        <div>
                                            <strong>
                                                <OwLabel title={"url"} postText=":" />
                                            </strong>{" "}
                                            {window.location.href}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {!isDevelopment && (
                        <div className="text-center text-muted small">
                            <p className="mb-0">
                                <OwLabel title={"if_this_problem_persists_please_contact_support"} />
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
